import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../../utils/hooks/useFacility";
import { GET_TICKET_BY_EXERCISE, GET_SCHEDULE_DETAILS, GET_SCHEDULE_DETAILS_NOLOGIN } from "../../../../api/apiRoutes";
import { State } from "../../../../store";
import { Assort } from "../../../../models/Assort";
import { Exercise } from "../../../../models/Exercise";
import { TicketAccess, setTicketAccessFromExercise } from "../../../../models/TicketAccess";
import getId from "../../../../../utils/getId";

interface ReservationState {
  tickets: Assort[];
  exerciseDetails: Exercise | null;
  loading: boolean;
  ticketAccess: TicketAccess;
  ticketLimit: number;
}

export default function useCourtReservation() {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const userDataId = useSelector((state: State) => state.auth.userData?.objectId);

  const [state, setState] = useState<ReservationState>({
    tickets: [],
    exerciseDetails: null,
    loading: false,
    ticketAccess: {} as TicketAccess,
    ticketLimit: 0,
  });

  const getReservationData = useCallback(
    async (exerciseId: number) => {
      setState((prev) => ({ ...prev, loading: true }));

      try {
        // Pobierz szczegóły exercise
        const route = userDataId
          ? GET_SCHEDULE_DETAILS(facilityIds, `${exerciseId}`, `0,${userDataId}`)
          : GET_SCHEDULE_DETAILS_NOLOGIN(facilityIds, `${exerciseId}`);

        const { result: exerciseResult, isError: exerciseError } = await get(route);

        if (exerciseError || !exerciseResult) {
          setState({
            tickets: [],
            exerciseDetails: null,
            loading: false,
            ticketAccess: {} as TicketAccess,
            ticketLimit: 0,
          });
          return;
        }

        // Pobierz bilety używając exerciseGroup.objectId
        const exerciseGroupIds = getId(exerciseResult.exerciseGroup);
        const { result: ticketsResult, isError: ticketsError } = await get(
          GET_TICKET_BY_EXERCISE(facilityIds, exerciseGroupIds)
        );

        // Oblicz ticketAccess i ticketLimit
        const ticketAccessTransform = setTicketAccessFromExercise(
          exerciseResult.objectId,
          exerciseResult.name.replace(/%/g, "%25"),
          exerciseResult.startTime
        );
        const calculatedTicketLimit =
          exerciseResult.capacity - (exerciseResult.reserved + exerciseResult.prereservation);

        setState({
          exerciseDetails: exerciseResult,
          tickets: ticketsError ? [] : ticketsResult || [],
          ticketAccess: ticketAccessTransform,
          ticketLimit: calculatedTicketLimit,
          loading: false,
        });
      } catch (error) {
        console.error("Error fetching reservation data:", error);
        setState({
          tickets: [],
          exerciseDetails: null,
          loading: false,
          ticketAccess: {} as TicketAccess,
          ticketLimit: 0,
        });
      }
    },
    [facilityIds, get, userDataId]
  );

  const getMinPrice = useCallback(() => {
    if (state.tickets.length === 0) return null;
    return Math.min(...state.tickets.map((ticket) => ticket.price));
  }, [state.tickets]);

  const resetData = useCallback(() => {
    setState({
      tickets: [],
      exerciseDetails: null,
      loading: false,
      ticketAccess: {} as TicketAccess,
      ticketLimit: 0,
    });
  }, []);

  return {
    ...state,
    getReservationData,
    getMinPrice,
    resetData,
  };
}
